import { useState, useEffect, useRef } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function FinancialDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const dashboardRef = useRef<HTMLDivElement>(null);

  // Formatage en GNF (change 'fr-GN' → 'fr-FR' et 'GNF' → '€' si besoin)
  const formatGNF = (value: number): string => {
    if (value === 0) return '0 GNF';
    return value.toLocaleString('fr-GN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + ' GNF';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Remplace ces URLs par tes vraies routes API Laravel
        const [treasuryRes, receivablesRes, payablesRes, agedRes, expensesRes, cashflowRes] = await Promise.all([
          getResources('Report/finance/treasury'),
          getResources('Report/finance/receivables'),
          getResources('Report/finance/payables'),
          getResources('Report/finance/aged-receivables'),
          getResources('Report/finance/expenses-breakdown'),
          getResources('Report/finance/cashflow-last-6-months'),
        ]);

        setData({
          treasury: treasuryRes.data,
          receivables: receivablesRes.data,
          payables: payablesRes.data,
          agedReceivables: agedRes.data,
          expenses: expensesRes.data,
          cashFlow: cashflowRes.data,
        });
      } catch (error) {
        console.error('Erreur chargement données financières:', error);
        // Fallback avec tes données statiques
        // setData({
        //   treasury: {
        //     cashBalance: 145680,
        //     bankBalance: 523450,
        //     totalBalance: 669130,
        //   },
        //   receivables: {
        //     total: 234560,
        //     overdue: 45890,
        //   },
        //   payables: {
        //     total: 156780,
        //     overdue: 23450,
        //   },
        //   agedReceivables: [
        //     { range: '0-30 jours', amount: 125670, percentage: 53.6 },
        //     { range: '31-60 jours', amount: 63000, percentage: 26.9 },
        //     { range: '61-90 jours', amount: 28340, percentage: 12.1 },
        //     { range: '> 90 jours', amount: 17550, percentage: 7.4 },
        //   ],
        //   expenses: [
        //     { category: 'Achats Marchandises', amount: 156780, percentage: 45.2 },
        //     { category: 'Salaires & Charges', amount: 89450, percentage: 25.8 },
        //     { category: 'Loyer & Charges', amount: 35600, percentage: 10.3 },
        //     { category: 'Électricité & Eau', amount: 12340, percentage: 3.6 },
        //     { category: 'Transport & Logistique', amount: 28900, percentage: 8.3 },
        //     { category: 'Autres Charges', amount: 23680, percentage: 6.8 },
        //   ],
        //   cashFlow: [
        //     { month: 'Jan', inflow: 285600, outflow: 198450 },
        //     { month: 'Fév', inflow: 312400, outflow: 215680 },
        //     { month: 'Mar', inflow: 298750, outflow: 203920 },
        //     { month: 'Avr', inflow: 325800, outflow: 228560 },
        //     { month: 'Mai', inflow: 342580, outflow: 245780 },
        //     { month: 'Juin', inflow: 358900, outflow: 256340 },
        //   ],
        // });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const maxCashFlow = data?.cashFlow
    ? Math.max(...data.cashFlow.flatMap((m: any) => [m.inflow, m.outflow]))
    : 1;

  // Export Excel réel
  const exportToExcel = () => {
    if (!data) return;

    const rows = [
      ['RAPPORT FINANCIER'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [],
      ['TRÉSORERIE'],
      ['Solde Caisse', formatGNF(data.treasury.cashBalance)],
      ['Solde Banque', formatGNF(data.treasury.bankBalance)],
      ['Trésorerie Totale', formatGNF(data.treasury.totalBalance)],
      [],
      ['CRÉANCES ET DETTES'],
      ['Créances Clients', formatGNF(data.receivables.total)],
      ['Créances en Retard', formatGNF(data.receivables.overdue)],
      ['Dettes Fournisseurs', formatGNF(data.payables.total)],
      ['Dettes en Retard', formatGNF(data.payables.overdue)],
      [],
      ['BALANCE ÂGÉE DES CRÉANCES'],
      ['Période', 'Montant', 'Pourcentage'],
      ...data.agedReceivables.map((r: any) => [r.range, formatGNF(r.amount), `${r.percentage}%`]),
      [],
      ['RÉPARTITION DES CHARGES'],
      ['Catégorie', 'Montant', 'Pourcentage'],
      ...data.expenses.map((e: any) => [e.category, formatGNF(e.amount), `${e.percentage}%`]),
      [],
      ['FLUX DE TRÉSORERIE (6 DERNIERS MOIS)'],
      ['Mois', 'Entrées', 'Sorties', 'Solde Net'],
      ...data.cashFlow.map((cf: any) => [
        cf.month,
        formatGNF(cf.inflow),
        formatGNF(cf.outflow),
        formatGNF(cf.inflow - cf.outflow),
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapport Financier');

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `rapport_financier_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Export PDF visuel
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    const canvas = await html2canvas(dashboardRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`rapport_financier_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-lg text-gray-500">Chargement du tableau de bord financier...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="text-red-600">Erreur de chargement des données.</div>;
  }

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* Header + Boutons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de Bord Financier</h2>
          <p className="text-sm text-gray-600 mt-1">Situation au {new Date().toLocaleDateString('fr-FR')}</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-pdf-line"></i>
            Export PDF
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-excel-line"></i>
            Export Excel
          </button>
        </div>
      </div>

      {/* Trésorerie */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-wallet-3-line text-2xl"></i>
            </div>
          </div>
          <p className="text-sm opacity-90 mb-1">Solde Caisse</p>
          <p className="text-3xl font-bold">{formatGNF(data.treasury.cashBalance)}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-bank-line text-2xl"></i>
            </div>
          </div>
          <p className="text-sm opacity-90 mb-1">Solde Banque</p>
          <p className="text-3xl font-bold">{formatGNF(data.treasury.bankBalance)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-2xl"></i>
            </div>
          </div>
          <p className="text-sm opacity-90 mb-1">Trésorerie Totale</p>
          <p className="text-3xl font-bold">{formatGNF(data.treasury.totalBalance)}</p>
        </div>
      </div>

      {/* Créances & Dettes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Créances Clients</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatGNF(data.receivables.total)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-arrow-down-circle-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Créances en Retard</p>
              <p className="text-2xl font-bold text-red-900 mt-1">{formatGNF(data.receivables.overdue)}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Dettes Fournisseurs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatGNF(data.payables.total)}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-arrow-up-circle-line text-2xl text-orange-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-orange-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600">Dettes en Retard</p>
              <p className="text-2xl font-bold text-orange-900 mt-1">{formatGNF(data.payables.overdue)}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-orange-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Balance âgée & Charges */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Balance Âgée des Créances</h3>
          <div className="space-y-4">
            {data.agedReceivables.map((item: any, i: number) => (
              <div key={i}>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{item.range}</span>
                  <span className="text-sm font-semibold">{formatGNF(item.amount)} ({item.percentage}%)</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      i === 0 ? 'bg-green-500' :
                      i === 1 ? 'bg-blue-500' :
                      i === 2 ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Répartition des Charges</h3>
          <div className="space-y-4">
            {data.expenses.map((exp: any, i: number) => (
              <div key={i}>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{exp.category}</span>
                  <span className="text-sm font-semibold">{formatGNF(exp.amount)} ({exp.percentage}%)</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                    style={{ width: `${exp.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Flux de trésorerie */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Flux de Trésorerie (6 derniers mois)</h3>
        <div className="space-y-6">
          {data.cashFlow.map((cf: any, i: number) => {
            const net = cf.inflow - cf.outflow;
            return (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 w-16">{cf.month}</span>
                  <div className="flex-1 flex items-center gap-6">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-green-600">Entrées</span>
                        <span className="font-medium text-green-700">{formatGNF(cf.inflow)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"
                          style={{ width: `${(cf.inflow / maxCashFlow) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-red-600">Sorties</span>
                        <span className="font-medium text-red-700">{formatGNF(cf.outflow)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full"
                          style={{ width: `${(cf.outflow / maxCashFlow) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-32 text-right">
                    <span className={`text-sm font-bold ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {net >= 0 ? '+' : ''}{formatGNF(net)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}